/**
 * LoadingScreen - Full-page loading indicator for route transitions
 *
 * Shows a branded loading experience while lazy-loaded routes are being fetched.
 * Designed to be fast and unobtrusive, respecting reduced motion preferences.
 */
export function LoadingScreen(): React.ReactElement {
  return (
    <div
      className="fixed inset-0 z-50 flex min-h-screen flex-col items-center justify-center bg-background-secondary"
      role="status"
      aria-live="polite"
      aria-label="Loading"
    >
      <div className="flex flex-col items-center gap-6">
        {/* Animated logo mark */}
        <div className="relative h-16 w-16">
          {/* Outer ring - pulsing */}
          <div
            className="absolute inset-0 rounded-2xl bg-sabi-teal-light"
            style={{
              animation: 'pulse 2s ease-in-out infinite',
            }}
          />

          {/* Brand mark */}
          <div className="absolute inset-0 flex items-center justify-center">
            <img
              src="/icons/logo-mark.png"
              alt=""
              className="h-10 w-10"
              width={40}
              height={40}
            />
          </div>
        </div>

        {/* Loading dots */}
        <div className="flex gap-1.5" aria-hidden="true">
          <span
            className="h-2 w-2 rounded-full bg-sabi-teal"
            style={{
              animation: 'bounce 1.4s ease-in-out infinite',
              animationDelay: '0s',
            }}
          />
          <span
            className="h-2 w-2 rounded-full bg-sabi-teal"
            style={{
              animation: 'bounce 1.4s ease-in-out infinite',
              animationDelay: '0.2s',
            }}
          />
          <span
            className="h-2 w-2 rounded-full bg-sabi-teal"
            style={{
              animation: 'bounce 1.4s ease-in-out infinite',
              animationDelay: '0.4s',
            }}
          />
        </div>

        {/* SR-only loading text */}
        <span className="sr-only">Loading, please wait...</span>
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.05); }
        }

        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-6px); }
        }

        @media (prefers-reduced-motion: reduce) {
          [style*="animation"] {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}
